import React, { useEffect, useState } from 'react';
import { PlusIcon, TrashIcon, UserIcon, StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { EmergencyContact } from '../../types/patient';
import { PhoneInput, EmailInput } from './';
import { UNREADABLE_CONTACT_TEXT } from '../../utils/patientContact';

interface RelationshipOption {
  value: EmergencyContact['relationship'];
  label: string;
  icon: string;
  description: string;
}

const RELATIONSHIP_OPTIONS: RelationshipOption[] = [
  { value: 'parent', label: 'Parent', icon: '👨‍👩‍👧', description: 'Mother or father' },
  { value: 'guardian', label: 'Guardian', icon: '🛡️', description: 'Legal guardian' },
  { value: 'spouse', label: 'Spouse/Partner', icon: '💑', description: 'Husband, wife, or partner' },
  { value: 'child', label: 'Child', icon: '👶', description: 'Son or daughter' },
  { value: 'sibling', label: 'Sibling', icon: '👫', description: 'Brother or sister' },
  { value: 'friend', label: 'Friend', icon: '👤', description: 'Close friend' },
  { value: 'other', label: 'Other', icon: '❓', description: 'Other relationship' },
];

interface EmergencyContactInputProps {
  value: EmergencyContact[];
  onChange: (contacts: EmergencyContact[]) => void;
  onValidationChange?: (isValid: boolean) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
  minContacts?: number;
  maxContacts?: number;
}

export const EmergencyContactInput: React.FC<EmergencyContactInputProps> = ({
  value = [],
  onChange,
  onValidationChange,
  label = 'Emergency Contacts',
  required = false,
  disabled = false,
  error,
  className = '',
  minContacts = 1,
  maxContacts = 5,
}) => {
  const [contactValidation, setContactValidation] = useState<Record<string, { firstName: boolean; lastName: boolean; phone: boolean; email: boolean }>>({});

  // With no contacts the field is valid unless required. Report it on mount too: otherwise a form
  // keeps whatever validity it started with until the user touches the contacts (AMP-144).
  // With contacts present, validity comes from each contact's own fields as they validate.
  useEffect(() => {
    if (value.length === 0) onValidationChange?.(!(required && minContacts > 0));
  }, [value.length, required, minContacts]);

  // Generate unique ID for new contacts
  const generateId = () => `ec_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

  // Validate all contacts
  const validateContacts = (contacts: EmergencyContact[]) => {
    if (required && contacts.length < minContacts) {
      onValidationChange?.(false);
      return false;
    }

    // Check if all contacts have valid data
    const allValid = contacts.every(contact => {
      const validation = contactValidation[contact.id];
      return validation?.firstName && validation?.lastName && validation?.phone && (validation?.email !== false);
    });

    // Ensure at least one primary contact if multiple contacts
    const hasPrimary = contacts.length <= 1 || contacts.some(c => c.isPrimary);

    const isValid = allValid && hasPrimary;
    onValidationChange?.(isValid);
    return isValid;
  };

  // Add new contact
  const addContact = () => {
    if (value.length >= maxContacts) return;

    const newContact: EmergencyContact = {
      id: generateId(),
      firstName: '',
      lastName: '',
      relationship: 'parent',
      phoneHash: '',
      emailHash: '',
      isPrimary: value.length === 0, // First contact is primary by default
      notes: '',
    };

    const newContacts = [...value, newContact];
    onChange(newContacts);
    setTimeout(() => validateContacts(newContacts), 100);
  };

  // Remove contact
  const removeContact = (contactId: string) => {
    const newContacts = value.filter(c => c.id !== contactId);
    
    // If we removed the primary contact, make the first remaining contact primary
    if (newContacts.length > 0 && !newContacts.some(c => c.isPrimary)) {
      newContacts[0].isPrimary = true;
    }
    
    onChange(newContacts);
    
    // Remove validation state for removed contact
    const newValidation = { ...contactValidation };
    delete newValidation[contactId];
    setContactValidation(newValidation);
    
    setTimeout(() => validateContacts(newContacts), 100);
  };

  // Update contact
  const updateContact = (contactId: string, updates: Partial<EmergencyContact>) => {
    const newContacts = value.map(contact => 
      contact.id === contactId ? { ...contact, ...updates } : contact
    );

    // If setting this contact as primary, unset others
    if (updates.isPrimary) {
      newContacts.forEach(contact => {
        if (contact.id !== contactId) {
          contact.isPrimary = false;
        }
      });
    }

    onChange(newContacts);
    setTimeout(() => validateContacts(newContacts), 100);
  };

  // Handle contact field validation
  const handleContactValidation = (contactId: string, field: 'firstName' | 'lastName' | 'phone' | 'email', isValid: boolean) => {
    setContactValidation(prev => ({
      ...prev,
      [contactId]: {
        ...prev[contactId],
        [field]: isValid
      }
    }));
    
    // Trigger overall validation after a short delay
    setTimeout(() => validateContacts(value), 100);
  };

  const getRelationshipOption = (relationship: EmergencyContact['relationship']) => {
    return RELATIONSHIP_OPTIONS.find(opt => opt.value === relationship) || RELATIONSHIP_OPTIONS[0];
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <label className="block text-lg font-medium text-gray-900">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <p className="text-sm text-gray-600 mt-1">
            {required
              ? `Add at least ${minContacts} emergency contact${minContacts !== 1 ? 's' : ''} who can be reached in case of emergency`
              : 'Optional: someone who can be reached in case of emergency'}
          </p>
        </div>
        
        {value.length < maxContacts && (
          <button
            type="button"
            onClick={addContact}
            disabled={disabled}
            className="btn-secondary flex items-center"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Add Contact
          </button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
          {error}
        </p>
      )}

      {/* Validation Error */}
      {required && value.length < minContacts && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
          At least {minContacts} emergency contact{minContacts !== 1 ? 's are' : ' is'} required
        </p>
      )}

      {/* No Primary Contact Warning */}
      {value.length > 1 && !value.some(c => c.isPrimary) && (
        <p className="text-sm text-yellow-600 bg-yellow-50 border border-yellow-200 rounded-md p-3">
          Please designate one contact as primary
        </p>
      )}

      {/* Contact Cards */}
      <div className="space-y-4">
        {value.map((contact, index) => {
          const relationshipOption = getRelationshipOption(contact.relationship);
          
          return (
            <div key={contact.id} className="card p-6 border-l-4 border-l-primary-500">
              {/* Contact Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <UserIcon className="w-5 h-5 text-gray-400" />
                    <h3 className="text-lg font-medium text-gray-900">
                      Emergency Contact {index + 1}
                    </h3>
                  </div>
                  
                  {/* Primary Contact Toggle */}
                  {value.length > 1 && (
                    <button
                      type="button"
                      onClick={() => updateContact(contact.id, { isPrimary: !contact.isPrimary })}
                      disabled={disabled}
                      className={`flex items-center space-x-1 px-2 py-1 rounded-md text-sm ${
                        contact.isPrimary 
                          ? 'bg-yellow-100 text-yellow-800 border border-yellow-300' 
                          : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
                      }`}
                      title={contact.isPrimary ? 'Primary contact' : 'Set as primary contact'}
                    >
                      {contact.isPrimary ? (
                        <StarIconSolid className="w-4 h-4" />
                      ) : (
                        <StarIcon className="w-4 h-4" />
                      )}
                      <span>{contact.isPrimary ? 'Primary' : 'Set Primary'}</span>
                    </button>
                  )}
                </div>

                {/* Remove Button: when contacts are optional, the last one can be removed too (AMP-144) */}
                {(!required || value.length > minContacts) && (
                  <button
                    type="button"
                    onClick={() => removeContact(contact.id)}
                    disabled={disabled}
                    className="text-red-600 hover:text-red-800 p-1 rounded-md hover:bg-red-50"
                    title="Remove contact"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                )}
              </div>

              {/* Contact Form */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* First Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={contact.firstName || ''}
                    onChange={(e) => {
                      updateContact(contact.id, { firstName: e.target.value });
                      handleContactValidation(contact.id, 'firstName', e.target.value.trim().length > 0);
                    }}
                    disabled={disabled}
                    placeholder="Enter first name"
                    className="input-field"
                    required
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={contact.lastName || ''}
                    onChange={(e) => {
                      updateContact(contact.id, { lastName: e.target.value });
                      handleContactValidation(contact.id, 'lastName', e.target.value.trim().length > 0);
                    }}
                    disabled={disabled}
                    placeholder="Enter last name"
                    className="input-field"
                    required
                  />
                </div>

                {/* Relationship */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Relationship <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={contact.relationship}
                    onChange={(e) => updateContact(contact.id, { 
                      relationship: e.target.value as EmergencyContact['relationship'] 
                    })}
                    disabled={disabled}
                    className="input-field"
                    required
                  >
                    {RELATIONSHIP_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.icon} {option.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {relationshipOption.description}
                  </p>
                </div>

                {/* Phone */}
                <PhoneInput
                  label="Phone Number"
                  value={contact.phoneHash}
                  onChange={(value) => updateContact(contact.id, { phoneHash: value })}
                  onValidationChange={(isValid) => handleContactValidation(contact.id, 'phone', isValid)}
                  // A phone saved before encryption is kept when left blank.
                  required={!contact.phoneUnreadable}
                  disabled={disabled}
                  placeholder={contact.phoneUnreadable ? UNREADABLE_CONTACT_TEXT : 'Emergency contact phone'}
                />

                {/* Email */}
                <EmailInput
                  label="Email Address"
                  value={contact.emailHash || ''}
                  onChange={(value) => updateContact(contact.id, { emailHash: value })}
                  onValidationChange={(isValid) => handleContactValidation(contact.id, 'email', isValid)}
                  required={false}
                  disabled={disabled}
                  placeholder={contact.emailUnreadable ? UNREADABLE_CONTACT_TEXT : 'Email (optional)'}
                />

                {/* Notes */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={contact.notes || ''}
                    onChange={(e) => updateContact(contact.id, { notes: e.target.value })}
                    disabled={disabled}
                    placeholder="Any additional information about this contact..."
                    rows={2}
                    className="input-field resize-none"
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Contact Button (bottom) */}
      {value.length === 0 && (
        <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
          <UserIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Emergency Contacts</h3>
          <p className="text-gray-600 mb-4">
            {required
              ? 'Add at least one emergency contact who can be reached in case of emergency'
              : 'None added. You can add one now or later.'}
          </p>
          <button
            type="button"
            onClick={addContact}
            disabled={disabled}
            className="btn-primary"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Add First Contact
          </button>
        </div>
      )}

      {/* Help Text */}
      <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-md p-3">
        <strong>Privacy Notice:</strong> Emergency contact phone numbers and emails are stored encrypted and only accessible to authorized healthcare providers.
      </div>
    </div>
  );
};